// File: src/index.tsx

import React from "react";
import ReactDOM from "react-dom/client";
import { createHashRouter, Navigate, RouterProvider } from "react-router-dom";
import { initializeIcons } from "@fluentui/react";
import { MsalProvider } from "@azure/msal-react";
import { PublicClientApplication, EventType, AccountInfo } from "@azure/msal-browser";
import { msalConfig, isAuthEnabled } from "./authConfig";
import "@fortawesome/fontawesome-free/css/all.min.css";
import "./index.css";
import "./styles/main-theme.css";
import "./styles/global-overrides.css";
import { ThemeProvider } from "./contexts/ThemeContext";
import { AuthProvider } from "./contexts/AuthContext";
import { ExampleLanguageProvider } from "./contexts/ExampleLanguageContext";
import { VoiceAutoSendProvider } from "./contexts/VoiceAutoSendContext";
import Layout from "./pages/layout/Layout";
import Chat from "./pages/chat/Chat";
import { LEGACY_REDIRECTS, PlatformAccess } from "./pages/platform/platformNav";
import { RequireAccess } from "./pages/platform/RequireAccess";
import type { AdminSectionKey } from "./pages/admin/AdminDashboard";

// Build ID injected at build time by Vite
declare const __BUILD_ID__: string;
declare const __BUILD_DATE__: string;

// Expose build info globally for debugging and cache verification
try {
    (window as any).APP_BUILD_ID = typeof __BUILD_ID__ !== "undefined" ? __BUILD_ID__ : "dev";
    (window as any).APP_BUILD_DATE = typeof __BUILD_DATE__ !== "undefined" ? __BUILD_DATE__ : "unknown";
    console.log(`🏗️ App Build: ${(window as any).APP_BUILD_ID}`);
} catch (e) {
    console.warn("Could not set app build info:", e);
}

// Always create MSAL instance — AuthProvider handles the enabled/disabled logic
const msalInstance = new PublicClientApplication(msalConfig);
if (isAuthEnabled) {
    if (!msalInstance.getActiveAccount() && msalInstance.getAllAccounts().length > 0) {
        msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
    }
    msalInstance.addEventCallback(event => {
        if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
            const account = event.payload as AccountInfo;
            msalInstance.setActiveAccount(account);
        }
    });
}

const layout = (
    <MsalProvider instance={msalInstance}>
        <AuthProvider>
            <Layout />
        </AuthProvider>
    </MsalProvider>
);

initializeIcons();

// ENG-032: every platform route is gated here with the same access level as its sidebar item (platformNav.ts).
const gated = (access: PlatformAccess, load: () => Promise<{ default: React.ComponentType }>) => async (): Promise<{ element: JSX.Element }> => {
    const { default: Page } = await load();
    return {
        element: (
            <RequireAccess access={access}>
                <Page />
            </RequireAccess>
        )
    };
};

const adminSection = (section: AdminSectionKey) => async (): Promise<{ element: JSX.Element }> => {
    const { default: AdminSection } = await import("./pages/admin/AdminDashboard");
    return {
        element: (
            <RequireAccess access="admin">
                <AdminSection section={section} />
            </RequireAccess>
        )
    };
};

// URL segment → section component in pages/admin/AdminDashboard.tsx (mounted unchanged inside the shell).
const ADMIN_SECTION_ROUTES: Array<[string, AdminSectionKey]> = [
    ["agents", "agents"],
    ["skills", "library"],
    ["agent-runtime", "agent"],
    ["routing", "routing"],
    ["connections", "connections"],
    ["models", "models"],
    ["search", "search"],
    ["latency", "latency"],
    ["audit", "audit"]
];

const router = createHashRouter([
    {
        path: "/",
        element: layout,
        children: [
            {
                index: true,
                element: <Chat />
            },
            {
                path: "qa",
                lazy: () => import("./pages/ask/Ask")
            },
            // ENG-032: the old stand-alone pages now live inside the admin platform shell.
            ...Object.entries(LEGACY_REDIRECTS).map(([from, to]) => ({ path: from.slice(1), element: <Navigate to={to} replace /> })),
            {
                path: "admin",
                lazy: async () => {
                    const module = await import("./pages/platform/PlatformShell");
                    return { Component: module.default };
                },
                children: [
                    { index: true, lazy: gated("observer", () => import("./pages/platform/overview/OverviewPage")) },
                    ...ADMIN_SECTION_ROUTES.map(([path, section]) => ({ path, lazy: adminSection(section) })),
                    { path: "voice", lazy: adminSection("voice") },
                    { path: "rating", lazy: gated("admin", () => import("./pages/platform/chat/RatingPage")) },
                    // ENG-067: the product intro's ready questions per product type.
                    { path: "ready-questions", lazy: gated("admin", () => import("./pages/platform/chat/ReadyQuestionsPage")) },
                    { path: "analytics", element: <Navigate to="/admin/analytics/overview" replace /> },
                    { path: "analytics/feedback", element: <Navigate to="/admin/analytics/overview" replace /> },
                    {
                        path: "analytics/sessions/:sessionId",
                        lazy: gated("observer", async () => ({ default: (await import("./pages/platform/observability/AnalyticsPage")).Component }))
                    },
                    {
                        path: "analytics/:section",
                        lazy: gated("observer", async () => ({ default: (await import("./pages/platform/observability/AnalyticsPage")).Component }))
                    },
                    { path: "*", lazy: () => import("./pages/NoPage") }
                ]
            },
            {
                path: "*",
                lazy: () => import("./pages/NoPage")
            }
        ]
    }
]);

ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
    <React.StrictMode>
        <ThemeProvider>
            <ExampleLanguageProvider>
                <VoiceAutoSendProvider>
                    <RouterProvider router={router} />
                </VoiceAutoSendProvider>
            </ExampleLanguageProvider>
        </ThemeProvider>
    </React.StrictMode>
);
